import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackInitiateCheckout, trackPurchase, trackSubscribe } from "@/utils/metaPixel";
import { trackTwitterSubscription } from "@/utils/twitterPixel";

// Supabase URL - matches the URL in client.ts
const SUPABASE_URL = "https://ancujowsxivyfnxhnsmr.supabase.co";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  CreditCard, 
  CheckCircle, 
  Clock,
  FileText,
  Link2,
  BarChart3,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Zap
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { User } from "@supabase/supabase-js";

interface Subscription {
  id: string;
  status: string;
  plan_name: string;
  plan_price_amount: number;
  plan_price_currency: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  polar_customer_id: string | null;
}

interface SubscriptionBenefit {
  id: string;
  benefit_type: string;
  benefit_value: number;
  benefit_limit: number | null;
  used_count: number;
  reset_period: string;
}

interface SubscriptionManagementProps {
  user: User | null;
}

const POLAR_PRODUCT_ID = '6937b0e2-6510-4b4c-9470-f28f981cd131';

export const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ user }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [benefits, setBenefits] = useState<SubscriptionBenefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (user) {
      loadSubscription();
    }
  }, [user]);

  // Poll subscription when returning from checkout
  useEffect(() => {
    // Check URL parameter FIRST (before checking user) to catch it even if user isn't loaded yet
    const params = new URLSearchParams(window.location.search);
    const isReturningFromCheckout = params.get('subscription') === 'success';
    
    // If we're returning from checkout but user isn't loaded yet, wait for user
    if (isReturningFromCheckout && !user) {
      console.log('[Meta Pixel] 🔄 Returning from checkout (Settings) - Waiting for user to load...');
      return; // Will re-run when user is loaded (user is in dependency array)
    }
    
    // If no checkout parameter, nothing to do
    if (!isReturningFromCheckout) {
      return;
    }
    
    // User is loaded and we're returning from checkout - start tracking
    if (user) {
      console.log('[Meta Pixel] 🔄 Returning from checkout (Settings) - Starting subscription polling...');
      console.log('[Meta Pixel] 📋 URL params:', Object.fromEntries(params.entries()));
      console.log('[Meta Pixel] 👤 User loaded:', user.id);
      
      // Remove the query parameter from URL
      window.history.replaceState({}, '', window.location.pathname);
      
      // Poll subscription data with exponential backoff
      let attempts = 0;
      const maxAttempts = 15;
      const baseInterval = 2000; // Start with 2 seconds
      let timeoutId: number | null = null;

      const pollSubscription = async () => {
        if (attempts >= maxAttempts) {
          console.log('Max polling attempts reached');
          toast.info('Subscription processing may take a few moments. Please refresh the page if it doesn\'t update.');
          return;
        }

        attempts++;
        await loadSubscription();
        
        // Check if subscription is now active by querying again
        const { data: subData } = await supabase
          .from('subscriptions')
          .select('status')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (subData && (subData.status === 'active' || subData.status === 'trialing')) {
          // Reload subscription to get full data
          await loadSubscription();
          
          // Track purchase/subscribe event ONLY when:
          // 1. Status is confirmed 'active' or 'trialing' (payment successful via Polar)
          // 2. This is a NEW subscription activation (not a renewal)
          // 3. We haven't tracked this subscription ID before
          const purchaseTracked = sessionStorage.getItem(`purchase_tracked_${subData.id}`);
          const subscriptionCreated = subData.created_at ? new Date(subData.created_at).getTime() : 0;
          const isNewSubscription = subscriptionCreated > 0 && (Date.now() - subscriptionCreated) < 24 * 60 * 60 * 1000; // Created within last 24 hours
          
          // Debug logging to help diagnose issues
          console.log('[Meta Pixel] 🔍 Checking subscription tracking conditions:', {
            subscriptionId: subData.id,
            status: subData.status,
            purchaseTracked: !!purchaseTracked,
            isNewSubscription: isNewSubscription,
            subscriptionAge: subscriptionCreated > 0 ? Math.round((Date.now() - subscriptionCreated) / 1000 / 60) : 0, // minutes
            created_at: subData.created_at
          });
          
          if (!purchaseTracked && isNewSubscription) {
            // Convert price from cents to dollars if needed (Polar stores in cents)
            // If price is > 100, assume it's in cents and convert
            const rawPrice = subData.plan_price_amount || 8790; // Default in cents: $87.90
            const price = rawPrice > 100 ? rawPrice / 100 : rawPrice; // Convert cents to dollars if needed
            const currency = subData.plan_price_currency || 'USD';
            
            // CRITICAL: Only track Purchase/Subscribe when payment is confirmed
            // Status 'active' or 'trialing' means Polar has confirmed successful payment
            // This prevents tracking for checkout attempts that don't result in payment
            console.log('[Meta Pixel] 🎯 Tracking Purchase/Subscribe event - Payment confirmed!', {
              subscriptionId: subData.id,
              status: subData.status,
              rawPrice: rawPrice,
              price: price,
              currency: currency
            });
            
            // Retry mechanism to ensure pixel is loaded before tracking
            let retryCount = 0;
            const maxRetries = 10; // Try for up to 5 seconds (10 * 500ms)
            
            const attemptTracking = () => {
              if (typeof window.fbq === 'undefined') {
                retryCount++;
                if (retryCount < maxRetries) {
                  console.log(`[Meta Pixel] ⏳ Pixel not loaded yet, retrying... (${retryCount}/${maxRetries})`);
                  setTimeout(attemptTracking, 500); // Retry every 500ms
                } else {
                  console.error('[Meta Pixel] ❌ Pixel failed to load after multiple retries. Events not sent.');
                  console.error('[Meta Pixel] 💡 This may be due to ad blocker or network issues.');
                  // Still mark as tracked to prevent infinite retries
                  sessionStorage.setItem(`purchase_tracked_${subData.id}`, 'true');
                }
                return;
              }
              
              // Pixel is loaded, fire events
              console.log('[Meta Pixel] 🎉 REAL SUBSCRIPTION DETECTED - Firing Purchase/Subscribe events!');
              console.log('[Meta Pixel] 📊 Subscription details:', {
                id: subData.id,
                status: subData.status,
                price: price,
                currency: currency,
                created_at: subData.created_at
              });
              
              trackPurchase({
                value: price,
                currency: currency,
                content_name: 'Subscription',
                content_category: 'Subscription',
                num_items: 1
              });
              
              trackSubscribe({
                value: price,
                currency: currency,
                content_name: 'Subscription',
                content_category: 'Subscription'
              });
              
              // Track Twitter/X Pixel subscription conversion
              trackTwitterSubscription({
                value: price,
                currency: currency,
                conversion_id: subData.id,
                email_address: user?.email || undefined
              });
              
              // Mark as tracked to prevent duplicate events
              sessionStorage.setItem(`purchase_tracked_${subData.id}`, 'true');
              sessionStorage.setItem(`twitter_subscription_tracked_${subData.id}`, 'true');
              console.log('[Meta Pixel] ✅ Purchase/Subscribe events tracked successfully for REAL subscription');
              console.log('[Twitter Pixel] ✅ Subscription conversion event tracked successfully');
              console.log('[Meta Pixel] 💡 Check Network tab → filter "facebook.com/tr" to verify requests were sent');
              console.log('[Twitter Pixel] 💡 Check Network tab → filter "ads-twitter.com" to verify requests were sent');
              console.log('[Meta Pixel] 💡 Check Meta Events Manager → Test Events tab (real-time)');
              console.log('[Twitter Pixel] 💡 Check Twitter Ads Manager → Events Manager');
            };
            
            // Start tracking attempt after initial delay
            setTimeout(attemptTracking, 100);
          } else {
            console.warn('[Meta Pixel] ⚠️ Skipping Purchase/Subscribe tracking:', {
              reason: purchaseTracked ? 'Already tracked' : !isNewSubscription ? 'Not a new subscription' : 'Unknown',
              purchaseTracked: !!purchaseTracked,
              isNewSubscription: isNewSubscription
            });
          }
          
          toast.success('Subscription activated successfully!');
          return;
        }

        // Otherwise, poll again after delay (exponential backoff)
        const delay = Math.min(baseInterval * Math.pow(1.2, attempts), 10000); // Max 10 seconds
        timeoutId = setTimeout(pollSubscription, delay) as unknown as number;
      };

      // Start polling after initial delay
      timeoutId = setTimeout(pollSubscription, 1000) as unknown as number;

      // Cleanup timeout on unmount
      return () => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [user]);

  const loadSubscription = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load subscription
      const { data: subData, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (subError && subError.code !== 'PGRST116') {
        throw subError;
      }

      if (subData) {
        setSubscription(subData);

        // Load benefits
        const { data: benefitsData, error: benefitsError } = await supabase
          .from('subscription_benefits')
          .select('*')
          .eq('subscription_id', subData.id);

        if (!benefitsError && benefitsData) {
          setBenefits(benefitsData);
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const createCheckout = async () => {
    if (!user) return;

    try {
      setCreatingCheckout(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to continue');
        return;
      }

      // Get user's country from profile if available
      const { data: profile } = await supabase
        .from('profiles')
        .select('country')
        .eq('user_id', user.id)
        .single();

      const response = await fetch(`${SUPABASE_URL}/functions/v1/polar-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          product_id: POLAR_PRODUCT_ID,
          success_url: `${window.location.origin}/settings?subscription=success`,
          country: profile?.country || undefined,
        }),
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!response.ok) {
        // Check if subscription was synced
        if (data.synced) {
          toast.success('Subscription synced! Refreshing...');
          await loadSubscription();
          return;
        }
        
        // Extract error message - handle both string and object formats
        let errorMessage = 'Failed to create checkout session';
        if (data.error) {
          if (typeof data.error === 'string') {
            errorMessage = data.error;
          } else if (data.error.message) {
            errorMessage = data.error.message;
          } else if (data.error.detail) {
            errorMessage = typeof data.error.detail === 'string' ? data.error.detail : JSON.stringify(data.error.detail);
          } else {
            errorMessage = JSON.stringify(data.error);
          }
        } else if (data.message) {
          errorMessage = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
        } else {
          errorMessage = `Failed to create checkout: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      // Check if subscription was synced (successful sync response)
      if (data.synced) {
        toast.success('Subscription synced! Refreshing...');
        await loadSubscription();
        return;
      }

      if (!data.checkout_url) {
        throw new Error('No checkout URL received from server');
      }

      // Track checkout initiation
      trackInitiateCheckout({
        content_name: 'Subscription Checkout',
        content_category: 'Subscription',
        value: 87.90, // Your subscription price
        currency: 'USD',
        num_items: 1
      });

      window.location.href = data.checkout_url;
    } catch (error: any) {
      console.error('Checkout error:', error);
      // Extract error message safely - handle both Error objects and plain objects
      let errorMessage = 'Failed to create checkout session. Please try again.';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.message) {
          errorMessage = typeof error.message === 'string' ? error.message : String(error.message);
        } else if (error.error) {
          errorMessage = typeof error.error === 'string' ? error.error : String(error.error);
        } else {
          errorMessage = String(error);
        }
      }
      toast.error(errorMessage);
      setCreatingCheckout(false);
    }
  };

  const syncSubscription = async () => {
    if (!user) return;

    try {
      setSyncing(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to continue');
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/polar-sync-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to sync subscription' }));
        let errorMessage = 'Failed to sync subscription';
        if (errorData.error) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          } else {
            errorMessage = JSON.stringify(errorData.error);
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Subscription synced successfully!');
        // Reload subscription data
        await loadSubscription();
      } else {
        throw new Error('Sync did not complete successfully');
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      let errorMessage = 'Failed to sync subscription. Please try again.';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.message) {
          errorMessage = typeof error.message === 'string' ? error.message : String(error.message);
        } else if (error.error) {
          errorMessage = typeof error.error === 'string' ? error.error : String(error.error);
        } else {
          errorMessage = String(error);
        }
      }
      toast.error(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const getCustomerPortalUrl = async () => {
    if (!user || !subscription?.polar_customer_id) return;

    try {
      setLoadingPortal(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to continue');
        return;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/polar-customer-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          customer_id: subscription.polar_customer_id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get portal URL' }));
        // Extract error message - handle both string and object formats
        let errorMessage = 'Failed to get customer portal URL';
        if (errorData.error) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error;
          } else if (errorData.error.message) {
            errorMessage = errorData.error.message;
          } else {
            errorMessage = JSON.stringify(errorData.error);
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (data.portal_url) {
        window.open(data.portal_url, '_blank');
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error: any) {
      console.error('Portal error:', error);
      // Extract error message safely - handle both Error objects and plain objects
      let errorMessage = 'Failed to open customer portal. Please try again.';
      if (error) {
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error.message) {
          errorMessage = typeof error.message === 'string' ? error.message : String(error.message);
        } else if (error.error) {
          errorMessage = typeof error.error === 'string' ? error.error : String(error.error);
        } else {
          errorMessage = String(error);
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoadingPortal(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      active: { label: 'Active', variant: 'default' },
      trialing: { label: 'Trialing', variant: 'default' },
      canceled: { label: 'Canceled', variant: 'destructive' },
      past_due: { label: 'Past Due', variant: 'destructive' },
      incomplete: { label: 'Incomplete', variant: 'secondary' },
      expired: { label: 'Expired', variant: 'destructive' },
    };

    const config = statusConfig[status] || { label: status, variant: 'secondary' as const };
    return (
      <Badge 
        variant={config.variant} 
        className={`rounded-full px-3 py-1.5 font-semibold text-sm ${
          config.variant === 'default' 
            ? 'bg-green-500/10 text-green-600 border-green-500/20' 
            : config.variant === 'destructive'
            ? 'bg-red-500/10 text-red-600 border-red-500/20'
            : ''
        }`}
      >
        {config.label}
      </Badge>
    );
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getBenefitIcon = (type: string) => {
    const icons: Record<string, React.ReactNode> = {
      article_generation: <FileText className="h-5 w-5" />,
      backlinks: <Link2 className="h-5 w-5" />,
      analysis: <Zap className="h-5 w-5" />,
      analytics: <BarChart3 className="h-5 w-5" />,
    };
    return icons[type] || <FileText className="h-5 w-5" />;
  };

  const getBenefitLabel = (type: string) => {
    const labels: Record<string, string> = {
      article_generation: 'Article Generation',
      backlinks: 'Backlinks',
      analysis: 'Analysis',
      analytics: 'Analytics Access',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isExpired = subscription?.status === 'expired' || subscription?.status === 'canceled';

  return (
    <Card className="
      bg-background/95
      backdrop-blur-lg
      border border-border/30
      shadow-[0_3px_16px_rgba(0,0,0,0.04),0_1px_6px_rgba(0,0,0,0.02)]
      hover:shadow-[0_6px_24px_rgba(0,0,0,0.06),0_2px_10px_rgba(0,0,0,0.03)]
      rounded-3xl
      transition-all duration-500 ease-out
    ">
      <CardHeader className="pb-6 pt-8 px-8">
        <CardTitle className="text-2xl font-bold text-foreground tracking-tight flex items-center gap-3">
          <div className="text-primary/80 p-3 rounded-2xl bg-primary/10 border border-primary/20 shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
            <CreditCard className="h-5 w-5" />
          </div>
          Subscription
        </CardTitle>
        <CardDescription className="text-muted-foreground text-base font-medium mt-2">
          Manage your subscription and view usage
        </CardDescription>
      </CardHeader>
      <CardContent className="px-8 pb-8 space-y-6">
        {/* Current Subscription Status */}
        {subscription ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
              <div className="flex-1 flex items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-foreground">{subscription.plan_name}</h3>
                    {getStatusBadge(subscription.status)}
                  </div>
                  {hasActiveSubscription && subscription.current_period_end && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>
                        {subscription.cancel_at_period_end 
                          ? `Renews until ${formatDate(subscription.current_period_end)}, then cancels`
                          : `Renews on ${formatDate(subscription.current_period_end)}`
                        }
                      </span>
                    </div>
                  )}
                  {subscription.canceled_at && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Canceled on {formatDate(subscription.canceled_at)}
                    </p>
                  )}
                </div>
                {hasActiveSubscription && (
                  <div className="flex-shrink-0 flex gap-2">
                    <Button
                      variant="outline"
                      className="rounded-full border-primary/20 hover:bg-primary/5"
                      onClick={getCustomerPortalUrl}
                      disabled={loadingPortal}
                    >
                      {loadingPortal ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Manage Subscription
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {isExpired && (
              <Alert className="rounded-2xl border-red-500/20 bg-red-500/5">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-600 font-medium">
                  Your subscription has expired. Subscribe to continue using premium features.
                </AlertDescription>
              </Alert>
            )}

          </div>
        ) : (
          <div className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-bold text-foreground">No Active Subscription</h3>
            </div>
            <p className="text-sm text-muted-foreground font-medium mb-4">
              You're currently on the free plan. Subscribe to access premium features.
            </p>
            <Button 
              className="rounded-full bg-gradient-to-r from-primary via-primary to-primary/90 px-6 py-3 text-base font-semibold hover:shadow-lg transition-all duration-300"
              onClick={createCheckout}
              disabled={creatingCheckout}
            >
              {creatingCheckout ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Subscribe Now'
              )}
            </Button>
          </div>
        )}

        {/* Subscription Benefits / Usage */}
        {hasActiveSubscription && benefits.length > 0 && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-foreground">Usage & Limits</h4>
            <div className="space-y-4">
              {benefits.filter(benefit => benefit.benefit_type !== 'analytics').map((benefit) => {
                const isUnlimited = benefit.benefit_limit === null;
                const usagePercent = isUnlimited 
                  ? 0 
                  : (benefit.used_count / benefit.benefit_limit) * 100;
                const remaining = isUnlimited 
                  ? 'Unlimited' 
                  : Math.max(0, benefit.benefit_limit - benefit.used_count);

                return (
                  <div 
                    key={benefit.id} 
                    className="p-6 rounded-3xl bg-gradient-to-br from-muted/30 to-muted/10 backdrop-blur-md border border-border/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.04)] transition-all duration-300"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="text-primary/80 p-2 rounded-xl bg-primary/10 border border-primary/20">
                          {getBenefitIcon(benefit.benefit_type)}
                        </div>
                        <span className="font-bold text-foreground">{getBenefitLabel(benefit.benefit_type)}</span>
                      </div>
                      <div className="text-sm font-semibold text-muted-foreground">
                        {benefit.used_count} / {isUnlimited ? '∞' : benefit.benefit_limit} used
                      </div>
                    </div>
                    {!isUnlimited && (
                      <Progress value={usagePercent} className="h-2 mb-2" />
                    )}
                    <div className="text-xs font-medium text-muted-foreground">
                      {remaining} remaining
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Subscribe Button for non-subscribers */}
        {!hasActiveSubscription && subscription && (
          <div className="p-6 rounded-3xl bg-gradient-to-br from-primary/10 to-primary/5 backdrop-blur-md border border-primary/20 shadow-[0_2px_8px_rgba(0,0,0,0.02)]">
            <h4 className="text-lg font-bold text-foreground mb-2">Upgrade to Premium</h4>
            <p className="text-sm text-muted-foreground font-medium mb-4">Get access to all premium features</p>
            <Button 
              className="w-full rounded-full bg-gradient-to-r from-primary via-primary to-primary/90 px-6 py-3 text-base font-semibold hover:shadow-lg transition-all duration-300"
              onClick={createCheckout}
              disabled={creatingCheckout}
            >
              {creatingCheckout ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                'Subscribe Now'
              )}
            </Button>
          </div>
        )}

        {/* Success Message */}
        {new URLSearchParams(window.location.search).get('subscription') === 'success' && !hasActiveSubscription && (
          <Alert className="rounded-2xl border-green-500/20 bg-green-500/5">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600 font-medium">
              Subscription successful! Your account is being updated. Please wait a moment...
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
